import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";
import dayjs from "dayjs";
import { Input, Checkbox, Button } from "@material-tailwind/react";

import LogsTable from "../../Component/LogsTable";
import Pagination from "../../Component/Pagination";
import service from "../../service";

type FormValues = {
  betId: string;
  status: boolean[];
  typeLogs: boolean[];
  event: boolean[];
  errorCode: boolean[];
  dateField: string;
};

const statusOptions = ["OK", "ERROR"];
const typeLogs = ["profile", "transfer"];
const eventOptions = [
  "betCancelled",
  "betRollback",
  "betSettlement",
  "betUpdate",
  "placeBet",
  "updateOdds",
];
const errorCodeOptions = [
  -999, 0, 1000, 1004, 1005, 1008, 1009, 1010, 9999
];


const TestAndLogs: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [dateRange, setDateRange] = useState<DateValueType>({
    startDate: dayjs().startOf("month").toDate(),
    endDate: dayjs().toDate(),
  });

  const [filters, setFilters] = useState<{
    betId?: string;
    startDate?: string;
    endDate?: string;
    status?: string[];
    typeLogs?: string[];
    event?: string[];
    errorCode?: number[];
    dateField?: string;
  } | null>(null);

  const { register, handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      betId: "",
      status: statusOptions.map(() => false),
      typeLogs: typeLogs.map((t) => t === "transfer"),
      event: eventOptions.map(() => false),
      errorCode: errorCodeOptions.map(() => false),
      dateField: "createDate",
    },
  });

  const onSubmit = (data: FormValues) => {
    const selectedStatuses = data.status
      .map((checked, idx) => (checked ? statusOptions[idx] : null))
      .filter((s): s is string => Boolean(s));

    const selectedTypeLogs = data.typeLogs
      .map((checked, idx) => (checked ? typeLogs[idx] : null))
      .filter((t): t is string => Boolean(t));

    const selectedEvents = data.event
      .map((checked, idx) => (checked ? eventOptions[idx] : null))
      .filter((e): e is string => Boolean(e));

    const selectedErrorCodes = data.errorCode
      .map((checked, idx) => (checked ? errorCodeOptions[idx] : null))
      .filter((n): n is number => typeof n === "number");

    const startIso = dateRange?.startDate
      ? dayjs(dateRange.startDate).startOf("day").toISOString()
      : undefined;
    const endIso = dateRange?.endDate
      ? dayjs(dateRange.endDate).endOf("day").toISOString()
      : undefined;

    setPage(1);
    setIsSubmitting(true);
    setFilters({
      betId: data.betId || undefined,
      startDate: startIso,
      endDate: endIso,
      status: selectedStatuses.length ? selectedStatuses : undefined,
      typeLogs: selectedTypeLogs.length ? selectedTypeLogs : undefined,
      event: selectedEvents.length ? selectedEvents : undefined,
      errorCode: selectedErrorCodes.length ? selectedErrorCodes : undefined,
      dateField: data.dateField,
    });
  };

  const { data, isFetching } = useQuery({
    queryKey: ["logs", page, limit, filters],
    queryFn: () =>
      service.getLogs({
        page,
        limit,
        ...(filters as object),
      }),
    keepPreviousData: true,
    enabled: !!filters,
  });

  useEffect(() => {
    if (!isFetching) {
      setIsSubmitting(false);
    }
  }, [isFetching, filters]);

  const total = data?.total || 0;
  const logs = data?.logs || [];

  return (
    <div className="min-w-0 space-y-4">
      <form
        className="grid grid-cols-1 gap-4 rounded-2xl border border-blue-gray-100 bg-white p-4 shadow-sm sm:grid-cols-2 sm:p-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Input
          label="Bet ID"
          variant="outlined"
          {...register("betId")}
          className="bg-white"
          crossOrigin={undefined}
        />

        <div className="min-w-0 w-full">
          <Datepicker
            primaryColor="green"
            value={dateRange}
            onChange={(v) => v && setDateRange(v)}
            showShortcuts
            separator="to"
            displayFormat="DD-MM-YYYY"
            maxDate={dayjs().endOf("month").toDate()}
          />
        </div>

        <div className="col-span-1 flex flex-wrap items-center gap-x-2 gap-y-0 sm:col-span-2">
          <span className="min-w-20 text-sm font-medium text-gray-700">Status:</span>
          {statusOptions.map((opt, idx) => (
            <Controller
              key={opt}
              control={control}
              name={`status.${idx}`}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  label={opt}
                  crossOrigin={undefined}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
          ))}
        </div>

        <div className="col-span-1 flex flex-wrap items-center gap-x-2 gap-y-0 sm:col-span-2">
          <span className="min-w-20 text-sm font-medium text-gray-700">Type Logs:</span>
          {typeLogs.map((opt, idx) => (
            <Controller
              key={opt}
              control={control}
              name={`typeLogs.${idx}`}
              render={({ field: { value = false, onChange } }) => (
                <Checkbox
                  label={opt}
                  crossOrigin={undefined}
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
          ))}
        </div>

        <div className="col-span-1 flex flex-wrap items-center gap-x-2 gap-y-0 sm:col-span-2">
          <span className="min-w-20 text-sm font-medium text-gray-700">Event:</span>
          {eventOptions.map((opt, idx) => (
            <Controller
              key={opt}
              control={control}
              name={`event.${idx}`}
              render={({ field: { value = false, onChange } }) => (
                <Checkbox
                  label={opt}
                  crossOrigin={undefined}
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
          ))}
        </div>

        <div className="col-span-1 flex flex-wrap items-center gap-x-2 gap-y-0 sm:col-span-2">
          <span className="min-w-20 text-sm font-medium text-gray-700">Error Code:</span>
          {errorCodeOptions.map((code, idx) => (
            <Controller
              key={code}
              control={control}
              name={`errorCode.${idx}`}
              render={({ field: { value = false, onChange } }) => (
                <Checkbox
                  label={String(code)}
                  crossOrigin={undefined}
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
          ))}
        </div>

        <div className="col-span-1 sm:col-span-2">
          <Button type="submit" className="w-full sm:w-auto">Submit</Button>
        </div>
      </form>

      {filters && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onClick={setPage}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
        />
      )}

      <div className="min-w-0 overflow-x-auto rounded-2xl">
        {!filters ? (
          <p className="text-center text-gray-500">Please submit filters to view logs.</p>
        ) : (isSubmitting || isFetching) ? (
          <p>Loading...</p>
        ) : (
          <LogsTable data={logs} />
        )}
      </div>

      {filters && logs.length > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onClick={setPage}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
        />
      )}
    </div>
  );
};

export default TestAndLogs;
